import type { Dispatch } from 'react';

import { clearPatientKeysFromIDB } from '../../crypto-module/clear-patient-keys-from-idb.js';
import { ApiError, ApiErrorKind } from '../../queue-module/api-error.js';
import { TechnicalErrorVariant } from '../technical-error-variant.js';
import type { CancelAction } from '../state-machine-feature/cancel-action.type.js';

import { POLL_INTERVAL_MS, RETRY_AFTER_MAX_MS, RETRY_AFTER_MIN_MS } from './poll-constants.js';

const MS_PER_SECOND = 1_000;

/**
 * Ergebnis der Fehler-Auswertung in einem Polling-Tick (T-9).
 *
 * - `abort`: `AbortError` (Unmount/Cleanup) — kein Dispatch, Loop ist fertig.
 * - `retry-rate-limited`: HTTP 429 — die Absage liegt sicher in der Queue,
 *   der Server bittet nur um Geduld. Nach `retryAfterMs` weiterpollen.
 * - `retry-network`: Fetch selbst scheiterte (Funkloch, Netzwechsel) — der
 *   Loop entscheidet anhand seines Zählers, ob wiederholt wird.
 * - `terminal`: echter Endzustand (404, 503, Schema, Unbekanntes) — Reducer
 *   steht auf `TECHNICAL_ERROR`, IDB ist aufgeräumt.
 */
export type PollErrorOutcome =
  | { readonly kind: 'abort' }
  | { readonly kind: 'terminal' }
  | { readonly kind: 'retry-network' }
  | { readonly kind: 'retry-rate-limited'; readonly retryAfterMs: number };

interface HandlePollErrorArgs {
  readonly error: unknown;
  readonly dispatch: Dispatch<CancelAction>;
}

/**
 * Mapped Fetch-/Schema-Fehler auf den passenden Loop-Ausgang.
 *
 * Transiente Fehler (429, Netzaussetzer) sind KEIN Endzustand mehr: die
 * Absage liegt zu diesem Zeitpunkt bereits in der Queue und wird von der
 * Praxis verarbeitet — ein sofortiger „Technischer Fehler" würde Patient:innen
 * eine gescheiterte Absage vorgaukeln (T-9). Nur echte Endzustände
 * dispatchen `GOTO_TECHNICAL_ERROR` und räumen die IDB.
 */
export async function handlePollError(args: HandlePollErrorArgs): Promise<PollErrorOutcome> {
  if (isAbortError(args.error)) {
    return { kind: 'abort' };
  }
  if (args.error instanceof ApiError && args.error.kind === ApiErrorKind.RateLimited) {
    return { kind: 'retry-rate-limited', retryAfterMs: toRetryDelayMs(args.error) };
  }
  if (args.error instanceof ApiError && args.error.kind === ApiErrorKind.Network) {
    return { kind: 'retry-network' };
  }
  args.dispatch({
    type: 'GOTO_TECHNICAL_ERROR',
    variant: TechnicalErrorVariant.Generic,
  });
  await safeClearIdb();
  return { kind: 'terminal' };
}

/** Server-Vorgabe in Millisekunden, geklemmt; ohne Header das Poll-Intervall. */
function toRetryDelayMs(error: ApiError): number {
  if (error.retryAfterSeconds === undefined) {
    return POLL_INTERVAL_MS;
  }
  const requestedMs = error.retryAfterSeconds * MS_PER_SECOND;
  return Math.min(RETRY_AFTER_MAX_MS, Math.max(RETRY_AFTER_MIN_MS, requestedMs));
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

async function safeClearIdb(): Promise<void> {
  try {
    await clearPatientKeysFromIDB();
  } catch {
    // IDB-Cleanup darf den Error-Pfad nicht verschlucken.
  }
}
